import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  width: 100%;
`;

const InputRow = styled.div`
  display: flex;
  gap: 1rem;

  input {
    flex: 1;
    padding: 1rem;
    border: 1px solid #dadada;
    font-size: 1.4rem;
    text-transform: uppercase;
    outline: none;
    &:focus {
      border: 1px solid #000;
    }
  }

  button {
    padding: 0 1.6rem;
    background-color: black;
    color: white;
    border: none;
    font-size: 1.3rem;
    text-transform: uppercase;
    cursor: pointer;
    white-space: nowrap;
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const Message = styled.p`
  font-size: 1.3rem;
  margin: 0;
  color: ${(props) => (props.$error ? "#d32f2f" : "#2e7d32")};
`;

const AppliedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f1f2f2;
  padding: 1rem 1.2rem;
  font-size: 1.4rem;

  span {
    font-weight: 600;
    color: #2e7d32;
  }

  button {
    background: none;
    border: none;
    color: #d32f2f;
    text-decoration: underline;
    cursor: pointer;
    font-size: 1.3rem;
    padding: 0;
  }
`;

// cartTotal (number) must be passed in — the current cart subtotal
// this component validates/applies the coupon against.
const CouponBox = ({ cartTotal }) => {
  const dispatch = useDispatch();
  const couponCode = useSelector((state) => state.couponCode);
  const couponDiscountPercent = useSelector(
    (state) => state.couponDiscountPercent
  );

  const [inputCode, setInputCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const applyCoupon = async () => {
    if (!inputCode.trim()) {
      setError("Please enter a coupon code");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/coupon/apply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: inputCode.trim(),
            cartTotal: cartTotal,
          }),
        }
      );
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || "Invalid coupon code");
        setLoading(false);
        return;
      }

      dispatch({
        type: "applyCoupon",
        code: data.data.code,
        discountPercent: data.data.discountPercent,
      });
      setInputCode("");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const removeCoupon = () => {
    dispatch({ type: "removeCoupon" });
    setError("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyCoupon();
    }
  };

  return (
    <Box>
      {!couponCode ? (
        <>
          <InputRow>
            <input
              type="text"
              placeholder="Enter coupon code"
              value={inputCode}
              onChange={(e) => {
                setError("");
                setInputCode(e.target.value.toUpperCase());
              }}
              onKeyDown={onKeyDown}
            />
            <button onClick={applyCoupon} disabled={loading}>
              {loading ? "Applying..." : "Apply"}
            </button>
          </InputRow>
          {error && <Message $error>{error}</Message>}
        </>
      ) : (
        <AppliedRow>
          <span>
            "{couponCode}" applied — {couponDiscountPercent}% off
          </span>
          <button onClick={removeCoupon}>Remove</button>
        </AppliedRow>
      )}
    </Box>
  );
};

export default CouponBox;
